// ── BACK-TO-TOP BUTTON ──
// Fixed bottom-right button that appears once the user has scrolled down.
//
// This app uses several different scroll containers depending on the page:
// some pages let the window scroll, others put `overflowY: auto` on an inner
// div (TranscriptLibraryPage, etc.). To work everywhere we listen for scroll
// events on the document in the CAPTURE phase — scroll events don't bubble,
// but they do fire from document → target during capture, so this catches
// scrolls on any descendant element regardless of which one is the actual
// scroller.

import { useEffect, useState } from 'react';

function findScrolledElement() {
  // Return the element with the largest current scrollTop. We check the
  // window/document candidates first (cheap), then walk every element with
  // overflow auto/scroll on the page.
  const candidates = [];
  candidates.push({ el: document.scrollingElement || document.documentElement, top: (document.scrollingElement || document.documentElement).scrollTop || 0 });
  candidates.push({ el: document.body, top: document.body.scrollTop || 0 });
  const root = document.getElementById('root');
  if (root) candidates.push({ el: root, top: root.scrollTop || 0 });

  // Walk all elements once and keep any whose scrollTop > 0 — covers
  // inner overflow:auto containers used on pages like TranscriptLibraryPage.
  const all = document.querySelectorAll('*');
  for (const el of all) {
    if (el.scrollTop > 0) candidates.push({ el, top: el.scrollTop });
  }
  candidates.sort((a, b) => b.top - a.top);
  return candidates[0];
}

export default function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    function check() {
      const winY = window.scrollY || window.pageYOffset || 0;
      const docTop = (document.scrollingElement || document.documentElement).scrollTop || 0;
      const bodyTop = document.body.scrollTop || 0;
      const root = document.getElementById('root');
      const rootTop = root ? (root.scrollTop || 0) : 0;
      // Also catch the inner scroll containers some pages use.
      let innerMax = 0;
      const scrollers = document.querySelectorAll('div');
      for (const el of scrollers) {
        if (el.scrollTop > innerMax) innerMax = el.scrollTop;
      }
      const maxScroll = Math.max(winY, docTop, bodyTop, rootTop, innerMax);
      setVisible(maxScroll > 200);
    }

    // Capture phase so we catch scrolls on ANY descendant element. Scroll
    // events don't bubble, but they DO fire on the way down during capture.
    document.addEventListener('scroll', check, { capture: true, passive: true });
    window.addEventListener('resize', check);
    check();

    return () => {
      document.removeEventListener('scroll', check, { capture: true });
      window.removeEventListener('resize', check);
    };
  }, []);

  function handleClick() {
    // Scroll every plausible candidate. The actual scroller varies by page,
    // so resetting them all is the safest cross-page approach.
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
    try { (document.scrollingElement || document.documentElement).scrollTop = 0; } catch {}
    try { document.body.scrollTop = 0; } catch {}
    const root = document.getElementById('root');
    if (root) {
      try { root.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
      try { root.scrollTop = 0; } catch {}
    }
    // Walk every element and reset any that have a non-zero scrollTop.
    // Covers inner overflow:auto containers used on TranscriptLibraryPage etc.
    const all = document.querySelectorAll('*');
    for (const el of all) {
      if (el.scrollTop > 0) {
        try { el.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
        try { el.scrollTop = 0; } catch {}
      }
    }
  }

  return (
    <button
      onClick={handleClick}
      aria-label="Back to top"
      style={{
        position: 'fixed',
        right: '18px',
        bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        zIndex: 9999,
        width: '46px',
        height: '46px',
        borderRadius: '50%',
        border: 'none',
        background: '#2e2019',
        color: '#fff',
        boxShadow: '0 4px 16px rgba(46,32,25,0.32)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '22px',
        lineHeight: 1,
        WebkitTapHighlightColor: 'transparent',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 180ms ease',
      }}
    >
      <span aria-hidden="true" style={{ marginTop: '-2px' }}>↑</span>
    </button>
  );
}
